import alamoBadge from "@/assets/badges/alamo-badge.webp?w=448&format=webp";
import alamoBadgeSet from "@/assets/badges/alamo-badge.webp?w=224;448&format=webp&as=srcset";
import hillCountryBadge from "@/assets/badges/hill-country-badge.webp?w=448&format=webp";
import hillCountryBadgeSet from "@/assets/badges/hill-country-badge.webp?w=224;448&format=webp&as=srcset";
import bluebonnetBadge from "@/assets/badges/bluebonnet-tent-badge.webp?w=448&format=webp";
import bluebonnetBadgeSet from "@/assets/badges/bluebonnet-tent-badge.webp?w=224;448&format=webp&as=srcset";
import longhornBadge from "@/assets/badges/longhorn-badge.webp?w=448&format=webp";
import longhornBadgeSet from "@/assets/badges/longhorn-badge.webp?w=224;448&format=webp&as=srcset";
import loneStarEliteBadge from "@/assets/badges/lone-star-elite-badge.webp?w=448&format=webp";
import loneStarEliteBadgeSet from "@/assets/badges/lone-star-elite-badge.webp?w=224;448&format=webp&as=srcset";
import trailBossBadge from "@/assets/badges/trail-boss-badge.webp?w=448&format=webp";
import trailBossBadgeSet from "@/assets/badges/trail-boss-badge.webp?w=224;448&format=webp&as=srcset";
import rangerCareBadge from "@/assets/badges/ranger-care-homestead-badge.webp?w=448&format=webp";
import rangerCareBadgeSet from "@/assets/badges/ranger-care-homestead-badge.webp?w=224;448&format=webp&as=srcset";
import republicBadge from "@/assets/badges/republic-series-badge.webp?w=448&format=webp";
import republicBadgeSet from "@/assets/badges/republic-series-badge.webp?w=224;448&format=webp&as=srcset";
import rioGrandeBadge from "@/assets/badges/rio-grande-hospitality-badge.webp?w=448&format=webp";
import rioGrandeBadgeSet from "@/assets/badges/rio-grande-hospitality-badge.webp?w=224;448&format=webp&as=srcset";
import revivalBadge from "@/assets/badges/revival-series-badge.webp?w=448&format=webp";
import revivalBadgeSet from "@/assets/badges/revival-series-badge.webp?w=224;448&format=webp&as=srcset";
import farmingRanchingBadge from "@/assets/badges/farming-ranching-badge.webp?w=448&format=webp";
import farmingRanchingBadgeSet from "@/assets/badges/farming-ranching-badge.webp?w=224;448&format=webp&as=srcset";
import outdoorRecreationBadge from "@/assets/badges/outdoor-recreation-badge.webp?w=448&format=webp";
import outdoorRecreationBadgeSet from "@/assets/badges/outdoor-recreation-badge.webp?w=224;448&format=webp&as=srcset";

const BADGES: Record<string, { url: string; srcSet: string; alt: string }> = {
  "alamo-series": { url: alamoBadge, srcSet: alamoBadgeSet, alt: "Alamo Series™ badge — Premium Retractable Awning" },
  "hill-country-series": { url: hillCountryBadge, srcSet: hillCountryBadgeSet, alt: "Hill Country Series™ badge — Motorized Patio Screen System" },
  "bluebonnet-series": { url: bluebonnetBadge, srcSet: bluebonnetBadgeSet, alt: "Bluebonnet Series™ badge — Tents, Vendor Booths & Outdoor Markets" },
  "longhorn-series": { url: longhornBadge, srcSet: longhornBadgeSet, alt: "Longhorn Series™ badge — Commercial Retractable System" },
  "republic-series": { url: republicBadge, srcSet: republicBadgeSet, alt: "Republic Series™ badge — Government & School Systems" },
  "rio-grande-series": { url: rioGrandeBadge, srcSet: rioGrandeBadgeSet, alt: "Rio Grande Series™ badge — Hospitality & Resorts: hotels, resorts, glamping, safari tents, spas, retreats and event venues" },
  "revival-series": { url: revivalBadge, srcSet: revivalBadgeSet, alt: "Revival Series™ badge — Service & Recovery" },
  "lone-star-elite": { url: loneStarEliteBadge, srcSet: loneStarEliteBadgeSet, alt: "Lone Star Series™ badge — Smart Lighting & Automation" },
  "trail-boss": { url: trailBossBadge, srcSet: trailBossBadgeSet, alt: "Trail Boss™ badge — Welding & Custom Fabrication" },
  "ranger-care": { url: rangerCareBadge, srcSet: rangerCareBadgeSet, alt: "Ranger Care™ badge — Membership Service & Maintenance" },
  "farming-ranching-series": { url: farmingRanchingBadge, srcSet: farmingRanchingBadgeSet, alt: "Agricultural Farming & Ranching Series™ badge — Texas Made Shade & Covers" },
  "outdoor-recreation-series": { url: outdoorRecreationBadge, srcSet: outdoorRecreationBadgeSet, alt: "Outdoor Recreation Series™ badge — Fishing, Hunting & Boating" },
};


/**
 * Badges whose artwork is very dark (black / deep navy) and therefore washes
 * out against our dark navy panels. These get extra brightness/contrast plus a
 * light halo so the silhouette separates from the background.
 */
const DARK_BADGES = new Set([
  "longhorn-series",
  "republic-series",
  "revival-series",
  "lone-star-elite",
]);

export function getSeriesBadge(slug: string) {
  return BADGES[slug];
}

interface Props {
  slug: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  /** Set true for the LCP hero badge on a page — eager + high priority. */
  priority?: boolean;
}

/**
 * Interactive collection brand badge. Idle wiggle draws attention;
 * hover intensifies motion + adds a subtle lift/scale.
 * Intrinsic 640×640 square keeps aspect ratio and prevents CLS.
 */
export function SeriesBadge({ slug, className = "", size = "lg", priority = false }: Props) {
  const badge = BADGES[slug];
  if (!badge) return null;
  const sizeClass =
    size === "sm" ? "w-24 md:w-28" : size === "md" ? "w-40 md:w-48" : "w-56 md:w-72";
  const sizesAttr =
    size === "sm" ? "112px" : size === "md" ? "192px" : "(min-width: 768px) 288px, 224px";
  const toneStyle = DARK_BADGES.has(slug)
    ? {
        filter:
          "brightness(1.35) contrast(1.08) saturate(1.05) drop-shadow(0 6px 12px rgba(0,0,0,0.45))",
        background:
          "radial-gradient(closest-side, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.75) 55%, rgba(255,255,255,0.18) 78%, rgba(255,255,255,0) 100%)",
      }
    : undefined;
  return (
    <img
      src={badge.url}
      srcSet={badge.srcSet}
      sizes={sizesAttr}
      alt={badge.alt}
      width={640}
      height={640}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "low"}
      style={toneStyle}
      className={`series-badge inline-block h-auto ${sizeClass} ${
        toneStyle ? "" : "brightness-125 drop-shadow-2xl"
      } ${className}`}
    />
  );
}
